// oxlint-disable better-tailwindcss/no-unknown-classes -- This adapter deliberately preserves legacy theme selectors.
import { createTheme, props } from '@stylexjs/stylex';
import clsx from 'clsx';
import { colors } from '@seeqdev/qomponents/theme/colors.stylex';
import { productAccents } from '@seeqdev/qomponents/theme/palette.stylex';
import { themes } from '@seeqdev/qomponents/theme/themes';
import type { QomponentsTheme } from '@seeqdev/qomponents';

export type LegacyTheme = QomponentsTheme | 'report' | 'default' | 'brand';

const productForLegacyTheme = {
  analysis: 'analysis',
  topic: 'topic',
  datalab: 'datalab',
  vantage: 'vantage',
  insights: 'insights',
  // The default SCSS theme inherits analysis; the brand/home screen uses topic blues.
  default: 'analysis',
  brand: 'topic',
  report: 'topic',
} satisfies Record<LegacyTheme, QomponentsTheme>;

// Webserver's legacy forms reset gives light inputs a white surface. Keep that host-specific
// behavior here; standalone qomponents fields continue to inherit their containing surface.
const topicLight = createTheme(colors, {
  timelineItemKind: productAccents.topicSubtle,
  vantageActiveButtonFocusBorder: productAccents.topicLight,
  suppressionComparatorFocusBorder: productAccents.topicLight,
  suppressionComparatorSelected: productAccents.topicLight,
  suppressionComparatorSelectedHover: productAccents.topicHighlight,
  suppressionComparatorSelectedPressed: productAccents.topicDark,
  suppressionComparatorSelectedBorder: productAccents.topicLight,
  suppressionComparatorSelectedHoverBorder: productAccents.topicHighlight,
  suppressionComparatorSelectedPressedBorder: productAccents.topicDark,
  tabLabel: productAccents.topicLight,
  segmentBorder: '#1f5f9f',
  segmentSelected: '#d9ecff',
  segmentSelectedHover: '#c7e3ff',
  alertBackground: `color-mix(in srgb, ${productAccents.topicHighlight} 10%, transparent)`,
  accent: productAccents.topicLight,
  accentHover: productAccents.topicHighlight,
  accentPressed: productAccents.topicDark,
  navigationTextActive: productAccents.topicLight,
  accentSubtle: productAccents.topicSubtle,
  link: productAccents.topicLight,
  icon: productAccents.topicIcon,
  iconForeground: productAccents.topicIcon,
  fieldBackground: '#ffffff',
  borderHover: productAccents.topicLight,
  borderFocus: productAccents.topicLight,
  borderActive: productAccents.topicLight,
});
const analysisLight = createTheme(colors, {
  timelineItemKind: productAccents.topicSubtle,
  vantageActiveButtonFocusBorder: productAccents.analysisLight,
  suppressionComparatorFocusBorder: productAccents.analysisLight,
  suppressionComparatorSelected: productAccents.analysisLight,
  suppressionComparatorSelectedHover: productAccents.analysisHighlight,
  suppressionComparatorSelectedPressed: productAccents.analysisDark,
  suppressionComparatorSelectedBorder: productAccents.analysisLight,
  suppressionComparatorSelectedHoverBorder: productAccents.analysisHighlight,
  suppressionComparatorSelectedPressedBorder: productAccents.analysisDark,
  tabLabel: productAccents.analysisLight,
  segmentBorder: productAccents.analysisLight,
  segmentSelected: productAccents.analysisSubtle,
  segmentSelectedHover: '#dff2ee',
  alertBackground: `color-mix(in srgb, ${productAccents.analysisHighlight} 10%, transparent)`,
  accent: productAccents.analysisLight,
  accentHover: productAccents.analysisHighlight,
  accentPressed: productAccents.analysisDark,
  navigationTextActive: productAccents.analysisLight,
  accentSubtle: productAccents.analysisSubtle,
  link: productAccents.analysisLight,
  icon: productAccents.analysisIcon,
  iconForeground: productAccents.analysisIcon,
  fieldBackground: '#ffffff',
  borderHover: productAccents.analysisLight,
  borderFocus: productAccents.analysisLight,
  borderActive: productAccents.analysisLight,
});
const datalabLight = createTheme(colors, {
  timelineItemKind: productAccents.topicSubtle,
  vantageActiveButtonFocusBorder: productAccents.datalabLight,
  suppressionComparatorFocusBorder: productAccents.datalabLight,
  suppressionComparatorSelected: productAccents.datalabLight,
  suppressionComparatorSelectedHover: productAccents.datalabHighlight,
  suppressionComparatorSelectedPressed: productAccents.datalabDark,
  suppressionComparatorSelectedBorder: productAccents.datalabLight,
  suppressionComparatorSelectedHoverBorder: productAccents.datalabHighlight,
  suppressionComparatorSelectedPressedBorder: productAccents.datalabDark,
  tabLabel: productAccents.datalabLight,
  segmentBorder: '#b95b22',
  segmentSelected: '#ffe6d6',
  segmentSelectedHover: '#ffd9bf',
  alertBackground: `color-mix(in srgb, ${productAccents.datalabHighlight} 10%, transparent)`,
  accent: productAccents.datalabLight,
  accentHover: productAccents.datalabHighlight,
  accentPressed: productAccents.datalabDark,
  navigationTextActive: productAccents.datalabLight,
  accentSubtle: productAccents.datalabSubtle,
  link: productAccents.datalabLight,
  icon: productAccents.datalabIcon,
  iconForeground: productAccents.datalabIcon,
  fieldBackground: '#ffffff',
  borderHover: productAccents.datalabLight,
  borderFocus: productAccents.datalabLight,
  borderActive: productAccents.datalabLight,
});
const vantageLight = createTheme(colors, {
  timelineItemKind: productAccents.vantageSubtle,
  vantageActiveButtonFocusBorder: productAccents.vantageLight,
  suppressionComparatorFocusBorder: productAccents.vantageLight,
  suppressionComparatorSelected: productAccents.vantageLight,
  suppressionComparatorSelectedHover: productAccents.vantageHighlight,
  suppressionComparatorSelectedPressed: productAccents.vantageDark,
  suppressionComparatorSelectedBorder: productAccents.vantageLight,
  suppressionComparatorSelectedHoverBorder: productAccents.vantageHighlight,
  suppressionComparatorSelectedPressedBorder: productAccents.vantageDark,
  tabLabel: productAccents.vantageLight,
  segmentBorder: productAccents.vantageLight,
  segmentSelected: productAccents.vantageSubtle,
  segmentSelectedHover: '#c5addd',
  alertBackground: `color-mix(in srgb, ${productAccents.vantageHighlight} 10%, transparent)`,
  accent: productAccents.vantageLight,
  accentHover: productAccents.vantageHighlight,
  accentPressed: productAccents.vantageDark,
  navigationTextActive: productAccents.vantageLink,
  accentSubtle: productAccents.vantageSubtle,
  link: productAccents.vantageLink,
  icon: productAccents.vantageIcon,
  iconForeground: productAccents.vantageIcon,
  fieldBackground: '#ffffff',
  borderHover: productAccents.vantageLight,
  borderFocus: productAccents.vantageLight,
  borderActive: productAccents.vantageLight,
});
const insightsLight = createTheme(colors, {
  timelineItemKind: productAccents.insightsSubtle,
  vantageActiveButtonFocusBorder: productAccents.insightsLight,
  suppressionComparatorFocusBorder: productAccents.insightsLight,
  suppressionComparatorSelected: productAccents.insightsLight,
  suppressionComparatorSelectedHover: productAccents.insightsHighlight,
  suppressionComparatorSelectedPressed: productAccents.insightsDark,
  suppressionComparatorSelectedBorder: productAccents.insightsLight,
  suppressionComparatorSelectedHoverBorder: productAccents.insightsHighlight,
  suppressionComparatorSelectedPressedBorder: productAccents.insightsDark,
  tabLabel: productAccents.insightsLight,
  segmentBorder: productAccents.insightsLight,
  segmentSelected: productAccents.insightsSubtle,
  segmentSelectedHover: '#b8dcff',
  alertBackground: `color-mix(in srgb, ${productAccents.insightsHighlight} 10%, transparent)`,
  accent: productAccents.insightsLight,
  accentHover: productAccents.insightsHighlight,
  accentPressed: productAccents.insightsDark,
  navigationTextActive: productAccents.insightsLight,
  accentSubtle: productAccents.insightsSubtle,
  link: productAccents.insightsLight,
  icon: productAccents.insightsIcon,
  iconForeground: productAccents.insightsIcon,
  fieldBackground: '#ffffff',
  borderHover: productAccents.insightsLight,
  borderFocus: productAccents.insightsLight,
  borderActive: productAccents.insightsLight,
});

export const compatibilityThemes = {
  topic: { light: topicLight, dark: themes.topic.dark },
  analysis: { light: analysisLight, dark: themes.analysis.dark },
  datalab: { light: datalabLight, dark: themes.datalab.dark },
  vantage: { light: vantageLight, dark: themes.vantage.dark },
  insights: { light: insightsLight, dark: themes.insights.dark },
};

/** StyleX portion for existing local boundaries that already supply their legacy classes. */
export function getControlThemeClassNames(dark: boolean, theme: LegacyTheme): string {
  const product = productForLegacyTheme[theme];
  return props(compatibilityThemes[product][dark ? 'dark' : 'light']).className ?? '';
}

/** Combine legacy and StyleX theme classes during migration; remove legacy classes once their consumers migrate. */
export function getCompatibilityThemeClassNames(dark: boolean, theme: LegacyTheme): string {
  return clsx(
    theme === 'brand' && `color_topic${dark ? '_dark' : ''}`,
    `color_${theme}`,
    dark && `color_${theme}_dark`,
    dark ? 'tw-dark' : 'light',
    getControlThemeClassNames(dark, theme),
  );
}

const legacyThemeClasses = new Set(
  (Object.keys(productForLegacyTheme) as LegacyTheme[]).flatMap((theme) =>
    [false, true].flatMap((dark) => getCompatibilityThemeClassNames(dark, theme).split(' ')),
  ),
);

/** Replace both theme systems while retaining unrelated classes on standalone page roots. */
export function replaceLegacyThemeClassNames(current: string, dark: boolean, theme: LegacyTheme): string {
  return clsx(
    current.split(/\s+/).filter((name) => !legacyThemeClasses.has(name)),
    getCompatibilityThemeClassNames(dark, theme),
  );
}
